import { useState, type ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { User, FileText, Home, CalendarCheck } from "lucide-react";
// ADV-1176 / ADR-0048: /service-agreements/:id is the recurring-agreement
// detail. Schedule dates (end / renewal / next visit / next bill) are
// DB-computed by finance.compute_service_agreement_dates — never written here.
// The Visits tab lists the jobs generated for the agreement's opportunity
// (the visit-generation engine is a separate ticket; until then this shows the
// opportunity's jobs).
import { useServiceAgreement, useJobs } from "@/hooks/use-api";
import { DetailPage } from "@/components/v2/detail-page";
import { DetailPageActions } from "@/components/v2/detail-page-actions";
import { KPIRow } from "@/components/v2/kpi-row";
import { StatusBadge } from "@/components/status-badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DataTable } from "@/components/v2/data-table";
import {
  EntityActivityFeed,
  EntityActivityPreview,
} from "@/components/v2/entity-activity";
import { formatCurrency, formatDate } from "@/lib/utils";

type Row = Record<string, unknown>;

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
        {value}
      </span>
    </div>
  );
}

function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function LinkedChip({
  icon,
  label,
  sublabel,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  sublabel: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-left hover:bg-[color:var(--color-elevated)] w-full sm:w-1/2"
    >
      <span className="shrink-0 text-[color:var(--color-text-md)]">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block truncate typo-body font-medium text-[color:var(--color-text-hi)]">
          {label}
        </span>
        <span className="block typo-label text-[color:var(--color-text-md)]">
          {sublabel}
        </span>
      </span>
    </button>
  );
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

function titleCase(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const visitColumns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "job_number",
    header: "Job",
    enableSorting: false,
    cell: ({ row }) => (
      <span className="typo-mono font-medium text-[color:var(--color-text-hi)]">
        {(row.original.job_number as string | undefined) || "—"}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.original.status as string | undefined;
      return value ? (
        <StatusBadge status={value} />
      ) : (
        <span className="text-[color:var(--color-text-lo)]">—</span>
      );
    },
  },
  {
    accessorKey: "scheduled_date",
    header: "Scheduled",
    enableSorting: false,
    cell: ({ row }) => {
      const value = row.original.scheduled_date as string | undefined;
      return (
        <span className="text-[color:var(--color-text-md)]">
          {value ? formatDate(value) : "—"}
        </span>
      );
    },
  },
];

// Lazily-mounted Visits tab — the jobs query only runs once the user opens the
// tab (Radix Tabs unmounts inactive TabsContent). Visits are the jobs tied to
// the agreement's sale opportunity; when there is no linked opportunity the
// empty state explains why nothing is listed yet.
function VisitsTab({
  opportunityId,
  onJobClick,
}: {
  opportunityId: string | null;
  onJobClick: (id: string) => void;
}) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 50;

  const { data, isLoading } = useJobs({
    deal_id: opportunityId ?? undefined,
    offset: pageIndex * pageSize,
    limit: pageSize,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
  });
  const jobs = (
    opportunityId ? ((data?.jobs as Row[] | undefined) ?? []) : []
  ) as Row[];
  const total = (opportunityId ? (data?.total as number | undefined) : 0) ?? 0;

  return (
    <DataTable
      columns={visitColumns}
      data={jobs}
      total={total}
      isLoading={opportunityId ? isLoading : false}
      onRowClick={(row) => onJobClick(row.id as string)}
      emptyMessage="No visit jobs scheduled yet"
      emptyDescription="Scheduled maintenance visits appear here as jobs once they are generated for this agreement's opportunity."
      pageSize={pageSize}
      pageIndex={pageIndex}
      onPaginationChange={setPageIndex}
      sorting={sorting}
      onSortingChange={setSorting}
    />
  );
}

export default function V2ServiceAgreementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  const { data, isLoading, isError } = useServiceAgreement(id ?? "");
  const agreement = (data as { service_agreement?: Row } | undefined)
    ?.service_agreement;

  const displayId = str(agreement?.display_id);
  const customerName = str(agreement?.customer_name);
  const customerContactId = str(agreement?.customer_contact_id);
  const planName = str(agreement?.plan_name);
  const planFrequency = str(agreement?.plan_billing_frequency);
  const status = str(agreement?.status);
  const startDate = str(agreement?.start_date);
  const endDate = str(agreement?.end_date);
  const renewalDate = str(agreement?.renewal_date);
  const nextVisitDate = str(agreement?.next_visit_date);
  const nextBillDate = str(agreement?.next_bill_date);
  const billingAmount = num(agreement?.billing_amount);
  const billingAnchorDay = num(agreement?.billing_anchor_day);
  const opportunityId = str(agreement?.opportunity_id);
  const propertyId = str(agreement?.property_id);
  const cancelledReason = str(agreement?.cancelled_reason);
  const cancelledAt = str(agreement?.cancelled_at);

  const title = displayId ? `Agreement ${displayId}` : "Service Agreement";

  const termWindow =
    startDate || endDate
      ? `${startDate ? formatDate(startDate) : "—"} – ${
          endDate ? formatDate(endDate) : "—"
        }`
      : "—";

  return (
    <DetailPage
      isLoading={isLoading}
      isError={isError}
      isNotFound={!isLoading && !isError && !agreement}
      title={title}
      subtitle={customerName ?? undefined}
      breadcrumbs={[
        { label: "Service Agreements", href: "/service-agreements" },
        { label: title },
      ]}
      notFoundMessage={{
        title: "Service agreement not found",
        description: "This service agreement may have been deleted.",
      }}
      actions={<DetailPageActions entityLabel="Service Agreement" />}
    >
      <Tabs
        defaultValue="overview"
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex flex-col"
      >
        <TabsList className="h-auto bg-transparent p-0 border-b border-[color:var(--color-border)] rounded-none w-full justify-start gap-0 mb-0">
          <TabsTrigger
            value="overview"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="visits"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Visits
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Activity
          </TabsTrigger>
        </TabsList>

        {/* ── Overview ── */}
        <TabsContent value="overview" className="mt-0 pt-6">
          <div className="flex gap-6">
            <div className="flex-1 min-w-0 space-y-5">
              {/* Inline properties strip */}
              <div className="flex items-center gap-1.5 flex-wrap typo-body text-[color:var(--color-text-md)]">
                {status && <StatusBadge status={status} />}
                {planName && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span>{planName}</span>
                  </>
                )}
                {agreement?.created_at != null && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span>
                      Created {formatDate(agreement.created_at as string)}
                    </span>
                  </>
                )}
              </div>

              {/* KPI row — the recurring schedule is DB-computed (next visit /
                  next bill), per ADR-0048. */}
              <KPIRow
                cards={[
                  { label: "Status", value: status ? titleCase(status) : "—" },
                  { label: "Plan", value: planName ?? "—" },
                  {
                    label: "Next Visit",
                    value: nextVisitDate ? formatDate(nextVisitDate) : "—",
                  },
                  {
                    label: "Next Bill",
                    value: nextBillDate ? formatDate(nextBillDate) : "—",
                  },
                ]}
              />

              {/* Schedule */}
              <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
                <div className="px-4 pt-3">
                  <span className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide">
                    Schedule
                  </span>
                </div>
                <div className="px-4 py-2">
                  <InfoRow
                    label="Start"
                    value={startDate ? formatDate(startDate) : null}
                  />
                  <InfoRow
                    label="End"
                    value={endDate ? formatDate(endDate) : null}
                  />
                  <InfoRow
                    label="Renews"
                    value={renewalDate ? formatDate(renewalDate) : null}
                  />
                  <InfoRow
                    label="Next Visit"
                    value={nextVisitDate ? formatDate(nextVisitDate) : null}
                  />
                  <InfoRow
                    label="Next Bill"
                    value={nextBillDate ? formatDate(nextBillDate) : null}
                  />
                  {!startDate && !endDate && !nextVisitDate && (
                    <p className="py-1 typo-body text-[color:var(--color-text-lo)]">
                      No schedule yet — set a start date to generate the
                      recurring schedule.
                    </p>
                  )}
                </div>
              </div>

              {/* Billing */}
              <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
                <div className="px-4 pt-3">
                  <span className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide">
                    Billing
                  </span>
                </div>
                <div className="px-4 py-2">
                  <InfoRow
                    label="Amount"
                    value={
                      billingAmount != null
                        ? formatCurrency(billingAmount)
                        : null
                    }
                  />
                  <InfoRow
                    label="Frequency"
                    value={planFrequency ? titleCase(planFrequency) : null}
                  />
                  <InfoRow
                    label="Anchor Day"
                    value={
                      billingAnchorDay != null ? String(billingAnchorDay) : null
                    }
                  />
                  {cancelledReason && (
                    <InfoRow label="Cancelled Reason" value={cancelledReason} />
                  )}
                  {cancelledAt && (
                    <InfoRow
                      label="Cancelled"
                      value={formatDate(cancelledAt)}
                    />
                  )}
                  {billingAmount == null && !planFrequency && (
                    <p className="py-1 typo-body text-[color:var(--color-text-lo)]">
                      Billing follows the linked plan.
                    </p>
                  )}
                </div>
              </div>

              {/* Linked records — plan shown as a non-link chip (no plan detail
                  page); opportunity / property / customer navigate out. */}
              <div className="flex flex-wrap gap-2">
                {(planName || planFrequency) && (
                  <span className="flex items-center gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 w-full sm:w-1/2">
                    <CalendarCheck className="h-4 w-4 shrink-0 text-[color:var(--color-text-md)]" />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate typo-body font-medium text-[color:var(--color-text-hi)]">
                        {planName ?? "Untitled plan"}
                      </span>
                      <span className="block typo-label text-[color:var(--color-text-md)]">
                        {planFrequency
                          ? `${titleCase(planFrequency)} plan`
                          : "Service Plan"}
                      </span>
                    </span>
                  </span>
                )}
                {opportunityId && (
                  <LinkedChip
                    icon={<FileText className="h-4 w-4" />}
                    label="View opportunity"
                    sublabel="Originating Sale"
                    onClick={() => navigate(`/opportunities/${opportunityId}`)}
                  />
                )}
                {propertyId && (
                  <LinkedChip
                    icon={<Home className="h-4 w-4" />}
                    label="View property"
                    sublabel="Serviced Property"
                    onClick={() => navigate(`/locations/${propertyId}`)}
                  />
                )}
                {customerName && customerContactId && (
                  <LinkedChip
                    icon={<User className="h-4 w-4" />}
                    label={customerName}
                    sublabel="Customer"
                    onClick={() => navigate(`/customers/${customerContactId}`)}
                  />
                )}
              </div>
            </div>

            {/* Right rail */}
            <div className="w-[300px] flex-none border-l border-[color:var(--color-border)] pl-6 space-y-5">
              <RailSection title="Properties">
                <InfoRow
                  label="Status"
                  value={status ? titleCase(status) : null}
                />
                <InfoRow label="Customer" value={customerName} />
                <InfoRow label="Plan" value={planName} />
                <InfoRow
                  label="Frequency"
                  value={planFrequency ? titleCase(planFrequency) : null}
                />
                <InfoRow label="Term" value={termWindow} />
                <InfoRow
                  label="Renews"
                  value={renewalDate ? formatDate(renewalDate) : null}
                />
                <InfoRow
                  label="Created"
                  value={
                    agreement?.created_at
                      ? formatDate(agreement.created_at as string)
                      : null
                  }
                />
              </RailSection>

              {/* Activity preview — compact 4-row rail */}
              {id && (
                <RailSection title="Activity">
                  <EntityActivityPreview
                    entityType="service_agreement"
                    entityId={id}
                  />
                </RailSection>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ── Visits ── (lazily mounted — see VisitsTab above) */}
        <TabsContent value="visits" className="mt-0 pt-6">
          {id && (
            <VisitsTab
              opportunityId={opportunityId}
              onJobClick={(jobId) => navigate(`/jobs/${jobId}`)}
            />
          )}
        </TabsContent>

        {/* ── Activity ── */}
        <TabsContent value="activity" className="mt-0 pt-6">
          {id && (
            <EntityActivityFeed
              entityType="service_agreement"
              entityId={id}
              emptyDescription="Messages, calls, and notes on this service agreement appear here once the first contact is made."
            />
          )}
        </TabsContent>
      </Tabs>
    </DetailPage>
  );
}
